// @flow

import React from 'react';

import { SIGNUP_PRE_INTENT_EVENT } from '@zapier/toolbox-tracking';

import AppNavContainer from 'app/common/components/AppNavContainer';
import HeaderAccountActions from 'app/viewport/components/Header/HeaderAccountActions';
import HeaderLinks from 'app/viewport/components/Header/HeaderLinks';
import HeaderLogo from 'app/viewport/components/Header/HeaderLogo';
import HeaderSearchToggle from 'app/viewport/components/Header/HeaderSearchToggle';
import HeaderServiceSearch from 'app/viewport/components/Header/HeaderServiceSearch';
import HeldTasksBannerContainer from 'app/dashboard/components/HeldTasksBannerContainer';
import OutsideClickable from 'app/common/components/OutsideClickable';
import { trackEvent } from 'app/track/trackingFunctions';
import {
  HeaderBadge,
  HeaderBlock,
  HeaderBlockContainer,
  HeaderBlockHeaderLinks,
  HeaderBlockInnerWrapper,
  HeaderBlockMenuControls,
  HeaderBlockOverlay,
  HeaderBlockServicesSearch,
  HeaderMenuButton,
  HeaderSignUpCta,
} from 'app/viewport/components/Header/HeaderBlock';

import type { Node } from 'react';

import type { HeaderLinkItemProps } from 'app/viewport/components/Header/HeaderLinks';

export type HeaderMediaQueries = {
  mobileLoggedOutMediaQuery: boolean,
  mobileLoggedInMediaQuery: boolean,
};

export type HeaderProps = {
  CustomComponent: () => Node,
  actions: HeaderLinkItemProps[],
  badgeText?: string,
  currentUrl: string,
  forceRefreshOnExit: boolean,
  isFullWidth: boolean,
  isHeldTasksBannerVisible: boolean,
  isLoggedIn: boolean,
  isMenuVisible: boolean,
  isMobileMenuEnabled: boolean,
  isServiceSearchVisible: boolean,
  isSubNavVisible: boolean,
  links: HeaderLinkItemProps[],
  matchesMediaQuery: HeaderMediaQueries,
  numberOfApps: number,
  onCloseServiceSearch: () => void,
  onHideMenu: () => void,
  onMount: () => void,
  onOpenServiceSearch: () => void,
  onTrack: (message: string, data: {}) => void,
  onRouteToService: Function,
  onToggleMenu: () => void,
  showServicesSearch: boolean,
  inUseServices: Array<string>,
};

const HeaderDefault = (props: HeaderProps) => {
  const {
    currentUrl,
    forceRefreshOnExit,
    isFullWidth,
    isLoggedIn,
    isSubNavVisible,
    isHeldTasksBannerVisible,
    isMobileMenuEnabled,
    showServicesSearch,
    links,
    onRouteToService,
    onTrack,
    numberOfApps,
    actions,
    badgeText,
    onToggleMenu,
    onOpenServiceSearch,
    onCloseServiceSearch,
    onHideMenu,
    isMenuVisible,
    isServiceSearchVisible,
    inUseServices,
  } = props;

  const subNav = !isSubNavVisible ? null : <AppNavContainer />;
  const heldTasksBannerContainer = !isHeldTasksBannerVisible ? null : (
    <HeldTasksBannerContainer inUseServices={inUseServices} />
  );

  const signUpCta = isLoggedIn ? null : (
    <HeaderSignUpCta
      href="/sign-up/"
      onClick={() => {
        trackEvent(SIGNUP_PRE_INTENT_EVENT, {
          signupPosition: 'header',
        });
      }}
    >
      Sign Up
    </HeaderSignUpCta>
  );

  const headerBadge = !badgeText ? null : (
    <HeaderBadge>{badgeText}</HeaderBadge>
  );

  // When a user is logged in, the services search will be hidden behind
  // a button that the user must click to reveal. The services search will
  // then be revealed as an overlay.
  const serviceSearchToggle = !isLoggedIn ? null : (
    <HeaderSearchToggle onClick={onOpenServiceSearch} />
  );

  // If a user is not logged in, then we do not display any account
  // information / notifications.
  const accountActions = !isLoggedIn ? null : <HeaderAccountActions />;

  const serviceSearchNode = !showServicesSearch ? null : (
    <OutsideClickable onClickOutside={onCloseServiceSearch}>
      <HeaderBlockServicesSearch kind={isLoggedIn ? 'disguised' : ''}>
        {serviceSearchToggle}
        <HeaderServiceSearch
          isVisible={isServiceSearchVisible}
          kind={{
            overlay: isLoggedIn,
            ...(!isServiceSearchVisible && isLoggedIn
              ? { hidden: true }
              : { visible: true }),
          }}
          onRouteToService={onRouteToService}
          onTrack={onTrack}
          placeholder={`Search ${numberOfApps}+ apps`}
        />
      </HeaderBlockServicesSearch>
    </OutsideClickable>
  );

  return (
    <HeaderBlock
      kind={{
        'logged-in': isLoggedIn,
        'logged-out': !isLoggedIn,
        'mobile-menu-open': isMenuVisible,
      }}
    >
      {/* Overlay for Services Search (visible when user is logged in) */}
      <HeaderBlockOverlay
        aria-hidden={true}
        href="#"
        kind={isServiceSearchVisible && isLoggedIn ? 'visible' : 'hidden'}
        onClick={e => {
          e.preventDefault();
          onCloseServiceSearch();
        }}
        tabIndex="-1"
      />

      <HeaderBlockContainer
        kind={{
          'search-enabled': showServicesSearch,
        }}
      >
        <HeaderBlockInnerWrapper
          kind={{
            'full-width': isFullWidth,
          }}
        >
          {/* Header Controls - Menu button (mobile), Logo, Signup CTA */}
          <HeaderBlockMenuControls kind={{ stretch: !showServicesSearch }}>
            <HeaderMenuButton
              iconBefore={isMenuVisible ? 'delete' : 'menu'}
              onClick={onToggleMenu}
            >
              {isMenuVisible ? 'Close' : 'Menu'}
            </HeaderMenuButton>

            <HeaderLogo isLoggedIn={isLoggedIn} />

            {headerBadge}
            {signUpCta}
          </HeaderBlockMenuControls>

          {/* Services Search */}
          {serviceSearchNode}

          {/* Navigation Links */}
          <HeaderBlockHeaderLinks
            kind={{
              open: isMenuVisible,
              stretch: !showServicesSearch,
            }}
          >
            <HeaderLinks
              currentUrl={currentUrl}
              forceRefreshOnExit={forceRefreshOnExit}
              isMobileMenu={isMobileMenuEnabled}
              kind={{
                links: true,
                'logged-in': isLoggedIn,
                'logged-out': !isLoggedIn,
              }}
              links={links}
              onClickLink={onHideMenu}
            />

            <HeaderLinks
              currentUrl={currentUrl}
              forceRefreshOnExit={forceRefreshOnExit}
              isMobileMenu={isMobileMenuEnabled}
              kind={{
                actions: true,
                'logged-in': isLoggedIn,
                'logged-out': !isLoggedIn,
              }}
              links={actions}
              onClickLink={onHideMenu}
            />
          </HeaderBlockHeaderLinks>

          {accountActions}
        </HeaderBlockInnerWrapper>
      </HeaderBlockContainer>

      {subNav}
      {heldTasksBannerContainer}
    </HeaderBlock>
  );
};

export default HeaderDefault;
